import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Syringe, Microscope, Shield, FlaskConical, Zap, Building2, ChevronDown } from "lucide-react";
import { BilingualEyebrow, BilingualHeading } from "@/components/ui/bilingual-heading";
import { useLanguage } from "@/contexts/LanguageContext";

const appMeta = [
  { id: "vaccine", key: "vaccine", icon: Syringe, color: "#EE1D25" },
  { id: "diagnostics", key: "diagnostics", icon: Microscope, color: "#10B981" },
  { id: "biodefense", key: "biodefense", icon: Shield, color: "#EF4444" },
  { id: "pandemic", key: "pandemic", icon: FlaskConical, color: "#FFB000" },
  { id: "faster", key: "faster", icon: Zap, color: "#8B5CF6" },
  { id: "access", key: "access", icon: Building2, color: "#EE1D25" },
];

export default function Applications() {
  const [expanded, setExpanded] = useState(null);
  const { t } = useLanguage();
  const apps = appMeta.map(a => ({
    ...a,
    title: t(`applications.apps.${a.key}T`),
    desc: t(`applications.apps.${a.key}D`),
  }));

  return (
    <section id="applications" data-testid="applications-section" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#EE1D25]/3 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16">
          <BilingualEyebrow en="Applications" hu="Alkalmaz&aacute;sok" className="text-[#9CA3AF]" />
          <BilingualHeading en="Platform Applications" hu="Platform alkalmaz&aacute;sok" colorClass="text-[#F3F4F6]" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2937]">
          {apps.map((app, i) => (
            <motion.div
              key={app.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#050505] p-6 md:p-8 group cursor-pointer transition-colors duration-300 hover:bg-[#0A0A0F]"
              onClick={() => setExpanded(expanded === app.id ? null : app.id)}
              data-testid={`app-card-${app.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded" style={{ background: `${app.color}12` }}>
                    <app.icon size={20} style={{ color: app.color }} />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-[#F3F4F6] leading-snug max-w-[200px]">{app.title}</h3>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-[#9CA3AF] transition-transform duration-300 shrink-0 ml-2 ${expanded === app.id ? 'rotate-180' : ''}`}
                />
              </div>
              <AnimatePresence>
                {expanded === app.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-sm text-[#9CA3AF] mt-4 leading-relaxed">{app.desc}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
